/**
 * ActivityDeck — the activity picker BottomSheet (spec §8.3).
 *
 * Search + category filter chips + duration choices (10 min – 10 uur) +
 * vertically scrolling catalogue + a sticky placement instruction that stays
 * visible while the list scrolls. Tapping an activity arms placement mode:
 * the deck closes and the next tap on the timeline places the block.
 */
import { useMemo, useState } from 'react'
import { BottomSheet, Chip, TextField } from '@/components/ui'
import { ActivityIcon } from '@/icons/activities'
import {
  ACTIVITY_CATEGORIES,
  searchActivities,
  type Activity,
} from '@/data/activities'
import { formatDuration } from './plannerDates'

/** Duration choices from 10 minutes through 10 hours (spec §5.2). */
export const DURATION_CHOICES_MINUTES = [
  10, 15, 30, 45, 60, 90, 120, 180, 240, 300, 360, 420, 480, 540, 600,
] as const

export const DEFAULT_DURATION_MINUTES = 60

export type ActivityDeckProps = {
  open: boolean
  onClose: () => void
  /** Arms placement mode with the chosen activity + duration. */
  onSelect: (activity: Activity, durationMinutes: number) => void
}

const ALL_CATEGORIES_ID = 'alle'

export function ActivityDeck({ open, onClose, onSelect }: ActivityDeckProps) {
  const [query, setQuery] = useState('')
  const [categoryId, setCategoryId] = useState<string>(ALL_CATEGORIES_ID)
  const [duration, setDuration] = useState<number>(DEFAULT_DURATION_MINUTES)

  const results = useMemo(
    () => searchActivities(query, categoryId === ALL_CATEGORIES_ID ? undefined : categoryId),
    [query, categoryId],
  )

  const categoryNameById = useMemo(
    () => new Map(ACTIVITY_CATEGORIES.map((category) => [category.id, category.name])),
    [],
  )

  return (
    <BottomSheet open={open} onClose={onClose} title="Activiteit toevoegen">
      <div className="planner-deck">
        <p className="planner-deck__instruction" role="note">
          Kies een duur en een activiteit. Tik daarna op een tijdstip in de tijdlijn om de
          activiteit te plaatsen.
        </p>

        <div className="planner-deck__search">
          <TextField
            label="Zoek een activiteit"
            value={query}
            onChange={setQuery}
            placeholder="Bijvoorbeeld: dweilen, wandelen, koffie"
          />
        </div>

        <div
          className="planner-deck__durations"
          role="group"
          aria-label="Kies een duur"
        >
          {DURATION_CHOICES_MINUTES.map((minutes) => (
            <Chip
              key={minutes}
              selected={duration === minutes}
              onClick={() => setDuration(minutes)}
            >
              {formatDuration(minutes)}
            </Chip>
          ))}
        </div>

        <div
          className="planner-deck__categories"
          role="group"
          aria-label="Filter op categorie"
        >
          <Chip
            selected={categoryId === ALL_CATEGORIES_ID}
            onClick={() => setCategoryId(ALL_CATEGORIES_ID)}
          >
            Alles
          </Chip>
          {ACTIVITY_CATEGORIES.map((category) => (
            <Chip
              key={category.id}
              selected={categoryId === category.id}
              onClick={() => setCategoryId(category.id)}
            >
              {category.name}
            </Chip>
          ))}
        </div>

        <div className="planner-deck__list-wrap">
          {results.length === 0 ? (
            <p className="planner-deck__empty">Geen activiteiten gevonden. Pas je zoekopdracht aan.</p>
          ) : (
            <ul className="planner-deck__list" aria-label="Activiteiten">
              {results.map((activity) => (
                <li key={activity.id}>
                  <button
                    type="button"
                    className="planner-deck__activity"
                    onClick={() => onSelect(activity, duration)}
                    aria-label={`${activity.name}, ${categoryNameById.get(activity.categoryId) ?? ''}, ${formatDuration(duration)}${activity.badge === 'warning' ? '. Let op: deze activiteit kan veel schermtijd met zich meebrengen.' : ''}`}
                  >
                    <span
                      className="planner-deck__activity-icon"
                      style={{ color: activity.color }}
                    >
                      <ActivityIcon
                        iconId={activity.iconId}
                        size={22}
                        showWarningBadge={activity.badge === 'warning'}
                      />
                    </span>
                    <span className="planner-deck__activity-text">
                      <span className="planner-deck__activity-name">{activity.name}</span>
                      <span className="planner-deck__activity-category">
                        {categoryNameById.get(activity.categoryId) ?? ''}
                      </span>
                    </span>
                    <span className="planner-deck__activity-duration">
                      {formatDuration(duration)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </BottomSheet>
  )
}
